import {
	ConstructionSiteFullDetails,
	PricingType,
	SanitizedWarrantySelection,
	TaskTemplateKey,
} from "@dune/types";
import {
	Button,
	Loader,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@dune/ui";
import { ClipboardCheck } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	useGetConstructionSiteBindersWarranties,
	useGetConstructionSiteTasks,
} from "../../../../api/queries/constructionSite.queries";
import { useMe } from "../../../../api/queries/users.queries";
import BinderWarrantyOfferContainer from "../../offers/BinderWarrantyOfferContainer";
import WarrantiesAccordion from "../../offers/WarrantiesAccordion";
import { PricingOptionsProvider } from "../../offers/contexts/PricingOptionsContext";
import BinderWarrantyOfferOptionsContainer from "../../offers/BinderWarrantyOfferOptionsContainer";
import { RoleEnum } from "../../../../../../../packages/vars/src";

interface TarificationTabProps {
	constructionSite: ConstructionSiteFullDetails;
}

const TarificationTab: React.FC<TarificationTabProps> = ({
	constructionSite,
}) => {
	const navigate = useNavigate();
	const { me: user } = useMe();
	const isInsurer = user?.role === RoleEnum.INSURER;
	const isDune = user?.role === RoleEnum.DUNE;
	const { data: constructionSiteBindersWarranties, isLoading } =
		useGetConstructionSiteBindersWarranties(Number(constructionSite.id));
	const { data: constructionSiteTasks } = useGetConstructionSiteTasks(
		constructionSite.id.toString(),
	);
	const [activeTab, setActiveTab] = useState<"souscription" | "reception">(
		"souscription",
	);

	const pricingType: PricingType =
		activeTab === "souscription" ? "INITIAL" : "ENDORSEMENT_RENEWAL";

	const handleTabChange = (value: string) => {
		if (value === "souscription" || value === "reception") {
			setActiveTab(value);
		}
	};
	const { availableBindersAndWarranties } =
		constructionSiteBindersWarranties || {};

	const constructionSiteOfferIds = useMemo(
		() =>
			constructionSite.constructionSiteOffers.map(
				(offer) => offer.binderWarranty.id,
			),
		[constructionSite.constructionSiteOffers],
	);

	const warranties = useMemo(
		() =>
			(Array.isArray(availableBindersAndWarranties) &&
				availableBindersAndWarranties
					?.flatMap((bw) => bw.warranties || [])
					.filter((warranty: SanitizedWarrantySelection) =>
						constructionSiteOfferIds.includes(warranty.id),
					)) ||
			[],
		[availableBindersAndWarranties, constructionSiteOfferIds],
	);

	const isReceptionTab = activeTab === "reception";

	const receptionAnalysisTask = constructionSiteTasks?.find(
		(task) =>
			task.templateKey === TaskTemplateKey.ANALYZE_CONSTRUCTION_SITE_RECEPTION,
	);
	const isReceptionAnalysisCompleted = receptionAnalysisTask?.status === "DONE";

	const hasReceptionPricing =
		isReceptionTab &&
		isReceptionAnalysisCompleted &&
		warranties.filter((w) => w.isAvailable).length > 0;

	const warrantyCards = useMemo(() => {
		if (warranties.length === 0) {
			return (
				<div className="flex flex-col gap-4 w-[30%] min-w-[300px] flex-shrink-0 ">
					<div className="flex flex-col gap-4 w-full">
						<p>Aucune garantie disponible pour ce chantier.</p>
					</div>
				</div>
			);
		}

		const availableWarranties = warranties.filter((w) => w.isAvailable);

		return availableWarranties.map(
			(binderWarranty: SanitizedWarrantySelection) => {
				const isChecked = constructionSiteOfferIds.includes(binderWarranty.id);

				return (
					<div
						className="flex flex-col gap-4 w-[30%] min-w-[300px] flex-shrink-0 "
						key={binderWarranty.id}
					>
						<BinderWarrantyOfferContainer
							binderWarranty={binderWarranty}
							constructionSiteId={constructionSite.id.toString()}
							storedPricingType={pricingType}
							checked={isChecked}
							isSyndic={user?.role === RoleEnum.SYNDIC}
						/>
						{!isReceptionTab && (
							<BinderWarrantyOfferOptionsContainer
								binderWarranty={binderWarranty}
								constructionSiteId={constructionSite.id.toString()}
								storedPricingType={pricingType}
							/>
						)}
						{!isReceptionTab && (
							<WarrantiesAccordion
								binderWarranty={binderWarranty}
								constructionSiteOffers={constructionSite.constructionSiteOffers}
							/>
						)}
					</div>
				);
			},
		);
	}, [
		warranties,
		constructionSite.id,
		constructionSiteOfferIds,
		constructionSite.constructionSiteOffers,
		pricingType,
		isReceptionTab,
		user?.role,
	]);

	if (isLoading) return <Loader />;
	if (!constructionSiteBindersWarranties) return null;

	const statusAllowsEdit =
		constructionSite.status === "NEW" ||
		constructionSite.status === "IN_PROGRESS" ||
		constructionSite.status === "TO_ANALYZE";

	return (
		<PricingOptionsProvider>
			<div className="flex flex-col gap-6">
				<div className="flex justify-between items-center">
					<div className="text-blue-badge-text text-2xl font-semibold">
						Tarification
					</div>
					{(isDune ||
						(!isInsurer &&
							!constructionSite.isSkipped &&
							statusAllowsEdit)) && (
						<Button
							onClick={() =>
								navigate(`/projects/edit/${constructionSite.id}/offers`)
							}
						>
							Modifier les garanties
						</Button>
					)}
				</div>

				<Tabs
					value={activeTab}
					onValueChange={handleTabChange}
					className="w-full"
				>
					<TabsList>
						<TabsTrigger value="souscription" variant="compact">
							Souscription
						</TabsTrigger>
						<TabsTrigger value="reception" variant="compact">
							Réception
						</TabsTrigger>
					</TabsList>

					<TabsContent value="souscription" className="mt-4">
						<div className="flex gap-6">
							<div className="flex-1 space-y-6">
								<div className="flex flex-row gap-6 w-full overflow-x-auto">
									{warrantyCards}
								</div>
							</div>
						</div>
					</TabsContent>

					<TabsContent value="reception" className="mt-4">
						{hasReceptionPricing ? (
							<div className="flex gap-6">
								<div className="flex-1 space-y-6">
									<div className="flex flex-row gap-6 w-full overflow-x-auto">
										{warrantyCards}
									</div>
								</div>
							</div>
						) : (
							<div className="text-center py-8">
								<ClipboardCheck className="h-16 w-16 text-gray-400 mx-auto mb-4" />
								<h3 className="text-lg font-semibold text-gray-600 mb-2">
									Aucune tarification disponible
								</h3>
								<p className="text-gray-500 mb-4">
									Aucune tarification de réception n'est disponible pour ce
									chantier.
								</p>
							</div>
						)}
					</TabsContent>
				</Tabs>
			</div>
		</PricingOptionsProvider>
	);
};

export default TarificationTab;
